/**
 * Pitch working-area + coord-conversion helpers.
 *
 * The drill canvas now scales by age group — Josh's hardcoded 105×68
 * pitch is replaced with a per-age-group PitchSpec. All overlay coords
 * derive from the active spec.
 *
 * Pitch is rendered LANDSCAPE: length on SVG x, width on SVG y, origin
 * at the centre spot. Attacking goal is on the LEFT (negative cx) by
 * convention, matching coaches' "attacking-left" stage view.
 */

import { PITCH_PADDING_M, type PitchSpec } from './pitchSpecs';

export type PitchView = 'full' | 'half_attacking' | 'half_defending' | 'quarter' | 'custom';

export interface CustomPitch {
  /** Width across the pitch (sideline-to-sideline). */
  width: number;
  /** Length down the pitch (goal-to-goal). */
  length: number;
  /** Display + entry unit. Storage is always metric. */
  unit: 'm' | 'ft';
}

const METRES_PER_FT = 0.3048;

/** The visible region of the pitch for a given view. Everything (the
 *  pitch backdrop, overlays, click handlers, Brain positions) is
 *  scoped to this region — when the coach picks "Custom 15×15 m",
 *  the canvas IS a 15×15 m grid, not a tiny patch on a full pitch. */
export interface VisibleRegion {
  /** Centre of the region in spec landscape coords. */
  cx: number;
  cy: number;
  /** Region dimensions in metres (length on x, width on y). */
  length: number;
  width: number;
  /** True when this is the full pitch (used to decide whether to draw
   *  full markings vs a grid). */
  isFull: boolean;
}

export function visibleRegion(spec: PitchSpec, view: PitchView, custom?: CustomPitch | null): VisibleRegion {
  const halfL = spec.length / 2;
  const halfW = spec.width / 2;
  if (view === 'half_attacking') {
    // Attacking goal is on the LEFT (negative cx) → attacking half is [-halfL, 0]
    return { cx: -halfL / 2, cy: 0, length: halfL, width: spec.width, isFull: false };
  }
  if (view === 'half_defending') {
    return { cx: halfL / 2, cy: 0, length: halfL, width: spec.width, isFull: false };
  }
  if (view === 'quarter') {
    return { cx: -halfL / 2, cy: -halfW / 2, length: halfL, width: halfW, isFull: false };
  }
  if (view === 'custom' && custom) {
    const wM = custom.unit === 'ft' ? feetToMetres(custom.width) : custom.width;
    const lM = custom.unit === 'ft' ? feetToMetres(custom.length) : custom.length;
    return { cx: 0, cy: 0, length: lM, width: wM, isFull: false };
  }
  return { cx: 0, cy: 0, length: spec.length, width: spec.width, isFull: true };
}

/** SVG viewBox string for the visible region — landscape, padded. */
export function pitchViewBox(spec: PitchSpec, view: PitchView = 'full', custom?: CustomPitch | null): string {
  const r = visibleRegion(spec, view, custom);
  const x = r.cx - r.length / 2 - PITCH_PADDING_M;
  const y = r.cy - r.width / 2 - PITCH_PADDING_M;
  const w = r.length + PITCH_PADDING_M * 2;
  const h = r.width + PITCH_PADDING_M * 2;
  return `${x} ${y} ${w} ${h}`;
}

/** Background hit-rect for click capture — sized to MATCH the viewBox
 *  exactly so the rect is fully painted (not clipped by SVG's default
 *  overflow:hidden). A rect with portions outside the viewBox is
 *  invisible to elementsFromPoint hit-testing. */
export function pitchBackgroundRect(spec: PitchSpec, view: PitchView = 'full', custom?: CustomPitch | null): { x: number; y: number; width: number; height: number } {
  const r = visibleRegion(spec, view, custom);
  return {
    x: r.cx - r.length / 2 - PITCH_PADDING_M,
    y: r.cy - r.width / 2 - PITCH_PADDING_M,
    width: r.length + PITCH_PADDING_M * 2,
    height: r.width + PITCH_PADDING_M * 2,
  };
}

/** Aspect ratio (length / width) of the visible region — used by the
 *  wrapper to size the pitch container so SVGs land at a known shape. */
export function pitchAspect(spec: PitchSpec, view: PitchView = 'full', custom?: CustomPitch | null): number {
  const r = visibleRegion(spec, view, custom);
  return (r.length + PITCH_PADDING_M * 2) / (r.width + PITCH_PADDING_M * 2);
}

/** Normalised [0..1] working-area coords → spec landscape SVG coords.
 *  drillX (0 left touchline, 1 right touchline) maps to SVG y.
 *  drillY (0 far/defending end, 1 near/attacking end) maps to SVG x (flipped).
 *  When view ≠ 'full' the [0..1] range is over the WORKING AREA, not the
 *  full pitch — so a player at [0.5, 0.5] is always the centre of the
 *  visible drill grid, no matter how small it is. */
export function drillNormToPitchSvg(
  p: readonly [number, number],
  spec: PitchSpec,
  view: PitchView = 'full',
  custom?: CustomPitch | null,
): { cx: number; cy: number } {
  const r = visibleRegion(spec, view, custom);
  const [drillX, drillY] = p;
  return {
    cx: r.cx + (0.5 - drillY) * r.length,
    cy: r.cy + (drillX - 0.5) * r.width,
  };
}

/** Landscape SVG coords → normalised [0..1] working-area coords. */
export function pitchSvgToDrillNorm(
  cx: number, cy: number,
  spec: PitchSpec,
  view: PitchView = 'full',
  custom?: CustomPitch | null,
): [number, number] {
  const r = visibleRegion(spec, view, custom);
  return [
    clamp01(0.5 + (cy - r.cy) / r.width),
    clamp01(0.5 - (cx - r.cx) / r.length),
  ];
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

export function metresToFeet(m: number): number {
  return Math.round((m / METRES_PER_FT) * 10) / 10;
}

export function feetToMetres(ft: number): number {
  return ft * METRES_PER_FT;
}

export const PITCH_VIEW_OPTIONS: { id: PitchView; label: string }[] = [
  { id: 'full', label: 'Full pitch' },
  { id: 'half_attacking', label: 'Half — attacking' },
  { id: 'half_defending', label: 'Half — defending' },
  { id: 'quarter', label: 'Quarter' },
  { id: 'custom', label: 'Custom…' },
];
